import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Music, Upload, Trash2, Check, Star } from "lucide-react";
import { toast } from "sonner";
import "./MusicPicker.css";

// Built-in beats are now fetched dynamically from GET /api/beats.
// Static fallback used only when the API is unreachable on first paint.
const FALLBACK_BEATS = [
  { id: "fallback-80", name: "80BPM BEAT", bpm: 80, url: "/beats/80bpm.mp3", is_default: true },
];

const STORAGE_KEY = "ff_selected_music_beat";
const FAVORITE_KEY = "ff_favorite_music_beat";

export default function MusicPicker({ backendApi, isAuthenticated, currentMp3, onSelect }) {
  const [open, setOpen] = useState(false);
  const [builtinBeats, setBuiltinBeats] = useState(FALLBACK_BEATS);
  const [userMp3s, setUserMp3s] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [favorite, setFavorite] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FAVORITE_KEY) || "null"); }
    catch { return null; }
  });
  const popoverRef = useRef(null);
  const fileInputRef = useRef(null);

  // Resolve a beat URL: if relative (/api/... or /beats/...), prefix appropriately.
  const resolveBeatUrl = useCallback((u) => {
    if (!u) return u;
    if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("blob:")) return u;
    if (u.startsWith("/api/")) {
      // Strip the /api prefix here because backendApi already ends with /api
      return `${backendApi}${u.substring(4)}`;
    }
    return u; // /beats/... served by the frontend
  }, [backendApi]);

  const getAuthHeader = () => {
    const token = localStorage.getItem("ff_auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch system beats (public)
  const fetchSystemBeats = useCallback(async () => {
    try {
      const resp = await axios.get(`${backendApi}/beats`);
      const beats = resp.data?.beats || [];
      if (beats.length > 0) setBuiltinBeats(beats);
    } catch (e) {
      // Keep fallback
    }
  }, [backendApi]);

  useEffect(() => { fetchSystemBeats(); }, [fetchSystemBeats]);
  // Re-fetch system beats every time the dropdown opens so admin changes appear immediately.
  useEffect(() => { if (open) fetchSystemBeats(); }, [open, fetchSystemBeats]);

  // Fetch user MP3 list
  const fetchUserMp3s = useCallback(async () => {
    if (!isAuthenticated) { setUserMp3s([]); return; }
    try {
      const resp = await axios.get(`${backendApi}/mp3`, { headers: getAuthHeader() });
      setUserMp3s(resp.data.files || []);
    } catch (e) {
      // Silent fail – not critical
    }
  }, [backendApi, isAuthenticated]);

  useEffect(() => { fetchUserMp3s(); }, [fetchUserMp3s]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // On mount, restore the favorite beat from localStorage (= "default at startup").
  // If no favorite, fallback to last selected beat from STORAGE_KEY for backward compat.
  // If neither, use the system default beat (is_default=true) so users get audio immediately.
  useEffect(() => {
    if (currentMp3) return;
    if (!builtinBeats || builtinBeats.length === 0) return;
    const saved = favorite || (() => {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); }
      catch { return null; }
    })();
    if (saved && saved.kind === "builtin") {
      // Fallback to system default (or first beat) if the saved id no longer exists
      const beat = builtinBeats.find(b => b.id === saved.id)
                || builtinBeats.find(b => b.is_default)
                || builtinBeats[0];
      if (beat) onSelect({ name: `${beat.name} (${beat.bpm} BPM)`, url: resolveBeatUrl(beat.url), isBuiltIn: true, beatId: beat.id });
      return;
    }
    if (saved && saved.kind === "user" && isAuthenticated) {
      const token = localStorage.getItem("ff_auth_token");
      fetchMp3AsBlobUrl(saved.id, token).then(url => {
        onSelect({ name: saved.name || "Mon MP3", url, isBuiltIn: false, isUserMp3: true, mp3Id: saved.id });
      }).catch(() => {});
      return;
    }
    // No saved choice: use the system default beat
    const def = builtinBeats.find(b => b.is_default) || builtinBeats[0];
    if (def) onSelect({ name: `${def.name} (${def.bpm} BPM)`, url: resolveBeatUrl(def.url), isBuiltIn: true, beatId: def.id });
  }, [isAuthenticated, builtinBeats]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFavorite = (e, kind, id, name) => {
    e.stopPropagation();
    if (favorite && favorite.kind === kind && favorite.id === id) {
      // Toggle off
      setFavorite(null);
      localStorage.removeItem(FAVORITE_KEY);
      toast.info("Favori supprimé");
    } else {
      const fav = { kind, id, name };
      setFavorite(fav);
      localStorage.setItem(FAVORITE_KEY, JSON.stringify(fav));
      toast.success(`Favori : ${name}`);
    }
  };

  const isFav = (kind, id) => favorite && favorite.kind === kind && favorite.id === id;

  const selectBuiltin = (beat) => {
    onSelect({ name: `${beat.name} (${beat.bpm} BPM)`, url: resolveBeatUrl(beat.url), isBuiltIn: true, beatId: beat.id });
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ kind: "builtin", id: beat.id }));
    setOpen(false);
  };

  const selectUserMp3 = (mp3) => {
    const token = localStorage.getItem("ff_auth_token");
    // Authenticated streaming URL — backend requires the bearer; we use a query param fallback
    // For simplicity here, we fetch as blob and assign object URL
    fetchMp3AsBlobUrl(mp3.id, token).then(url => {
      onSelect({ name: mp3.original_filename || "Mon MP3", url, isBuiltIn: false, isUserMp3: true, mp3Id: mp3.id });
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ kind: "user", id: mp3.id }));
    }).catch(() => toast.error("Impossible de charger ce MP3"));
    setOpen(false);
  };

  const fetchMp3AsBlobUrl = async (id, token) => {
    const resp = await fetch(`${backendApi}/mp3/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) throw new Error("fetch failed");
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
  };

  const onPickFile = () => {
    if (!isAuthenticated) {
      toast.info("Connecte-toi pour uploader tes propres MP3");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".mp3")) {
      toast.error("Fichier .mp3 uniquement");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (20 Mo max)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const resp = await axios.post(`${backendApi}/mp3/upload`, form, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`MP3 ajouté : ${resp.data.filename}`);
      await fetchUserMp3s();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur upload";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce MP3 définitivement ?")) return;
    try {
      await axios.delete(`${backendApi}/mp3/${id}`, { headers: getAuthHeader() });
      toast.success("MP3 supprimé");
      await fetchUserMp3s();
      // If we just deleted the currently-playing track, fallback to default beat
      if (currentMp3?.mp3Id === id) {
        const def = builtinBeats.find(b => b.is_default) || builtinBeats[0];
        if (def) selectBuiltin(def);
      }
      // Clear favorite if it was this MP3
      if (favorite && favorite.kind === "user" && favorite.id === id) {
        setFavorite(null);
        localStorage.removeItem(FAVORITE_KEY);
      }
    } catch (e) {
      toast.error("Erreur suppression");
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const currentLabel = currentMp3?.name?.split(" (")[0] || "Choisir";

  return (
    <div className="music-picker" ref={popoverRef}>
      <button
        type="button"
        className="music-picker-btn"
        onClick={() => setOpen(v => !v)}
        data-testid="music-picker-btn"
        title="Choisir la musique"
      >
        <Music size={14} />
        <span className="music-picker-label">{currentLabel}</span>
      </button>

      {open && (
        <div className="music-picker-dropdown" data-testid="music-picker-dropdown">
          <div className="music-picker-section-title">Beats intégrés</div>
          {builtinBeats.map((b) => {
            const active = currentMp3?.beatId === b.id;
            const fav = isFav("builtin", b.id);
            return (
              <div
                key={b.id}
                className={`music-picker-item ${active ? "active" : ""}`}
                onClick={() => selectBuiltin(b)}
                data-testid={`music-picker-builtin-${b.id}`}
                role="button"
              >
                {active && <Check size={14} className="mp-check" />}
                <span className="mp-name">{b.name}</span>
                <span className="mp-bpm">{b.bpm} BPM</span>
                <button
                  type="button"
                  className={`mp-fav-btn ${fav ? "is-fav" : ""}`}
                  onClick={(e) => toggleFavorite(e, "builtin", b.id, b.name)}
                  title={fav ? "Retirer des favoris" : "Définir comme favori par défaut"}
                  data-testid={`music-picker-fav-${b.id}`}
                >
                  <Star size={14} fill={fav ? "currentColor" : "none"} />
                </button>
              </div>
            );
          })}

          {isAuthenticated && userMp3s.length > 0 && (
            <>
              <div className="music-picker-section-title">Mes MP3</div>
              {userMp3s.map((mp3) => {
                const active = currentMp3?.mp3Id === mp3.id;
                const fav = isFav("user", mp3.id);
                return (
                  <div
                    key={mp3.id}
                    className={`music-picker-item ${active ? "active" : ""}`}
                    onClick={() => selectUserMp3(mp3)}
                    data-testid={`music-picker-user-${mp3.id}`}
                    role="button"
                  >
                    {active && <Check size={14} className="mp-check" />}
                    <span className="mp-name">{mp3.original_filename}</span>
                    <span className="mp-bpm">{formatSize(mp3.size)}</span>
                    <button
                      type="button"
                      className={`mp-fav-btn ${fav ? "is-fav" : ""}`}
                      onClick={(e) => toggleFavorite(e, "user", mp3.id, mp3.original_filename)}
                      title={fav ? "Retirer des favoris" : "Définir comme favori par défaut"}
                      data-testid={`music-picker-fav-${mp3.id}`}
                    >
                      <Star size={14} fill={fav ? "currentColor" : "none"} />
                    </button>
                    <button
                      type="button"
                      className="mp-delete-btn"
                      onClick={(e) => { e.stopPropagation(); handleDelete(mp3.id); }}
                      title="Supprimer ce MP3"
                      data-testid={`music-picker-delete-${mp3.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </>
          )}

          <div className="music-picker-actions">
            <button
              type="button"
              className="music-picker-upload"
              onClick={onPickFile}
              disabled={uploading}
              data-testid="music-picker-upload-btn"
            >
              <Upload size={14} />
              {uploading ? "Upload..." : "Ajouter un MP3"}
            </button>
          </div>

          {!isAuthenticated && (
            <div className="music-picker-hint">
              Connecte-toi pour ajouter tes propres MP3 (10 max, 20 Mo).
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="audio/mpeg,audio/mp3,.mp3"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
        </div>
      )}
    </div>
  );
}
